import { faCalculatorSimple, faPenSwirl } from '@fortawesome/pro-regular-svg-icons'
import {
  faBoltLightning,
  faBook,
  faBookOpen,
  faChalkboardUser,
  faComments,
  faDumbbell,
  faHeadSideBrain,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons'

import {
  COURSE_CHALLENGE_TOPIC,
  SAT_BOOTCAMP_MATH_COURSE,
  SAT_BOOTCAMP_MATH_TOPIC,
  SAT_BOOTCAMP_ONBOARDING_TEMPLATE_SLUG,
  SAT_BOOTCAMP_READING_COURSE,
  SAT_BOOTCAMP_READING_TOPIC,
  SAT_PREP_COURSE,
  SAT_PREP_MATH_TOPIC,
  SAT_PREP_READING_TOPIC,
} from '@/queries/constants'
import type {
  ProgramDetails,
  ProgramMarketingConfig,
  ProgramOfferingInstance,
  ProgramOfferingTemplate,
} from '@/client/services/programs/types'
import composeRichText from '@/client/services/richText/composeRichText'
import { getPublicUrl } from '@/client/services/url'
import { SAT_BOOTCAMP_WORLD_SLUG, SAT_PREP_WORLD_SLUG } from '@/client/services/worlds/constants'

export const SAT_BOOTCAMP_SWITCH_MAX_PER_MONTH = 2

export type ProgramOfferingWithCourse = ProgramOfferingInstance & {
  courseName: typeof SAT_BOOTCAMP_MATH_COURSE | typeof SAT_BOOTCAMP_READING_COURSE
}

export const MAX_SAT_BOOTCAMP_SESSION_RESCHEDULES_ALLOWED = 2

export const WEEKS_UNTIL_CLEAN_UP_BOOTCAMP = 2

export const NUM_SESSIONS_PER_BOOTCAMP = 8

export const SAT_AFFIRM_INPUTS_MESSAGE = `To participate in this study, we require that you affirm "yes" to all yes/no questions.`
export const SAT_SCORE_BAND_MESSAGE =
  'This bootcamp will have material tailored towards learners who are currently scoring in this range on this section of the SAT.'

export const SAT_BOOTCAMP_INVITE_CODE_LENGTH = 8

export const SAT_MATH_TOPICS_TO_CERTIFY = [
  {
    courseName: SAT_BOOTCAMP_MATH_COURSE,
    topicName: SAT_BOOTCAMP_MATH_TOPIC,
  },
  {
    courseName: SAT_PREP_COURSE,
    topicName: SAT_PREP_MATH_TOPIC,
  },
]

export const ELIGIBLE_TOPICS_FOR_SAT_MATH_CERTIFICATION = [
  ...SAT_MATH_TOPICS_TO_CERTIFY,
  {
    courseName: SAT_PREP_COURSE,
    topicName: COURSE_CHALLENGE_TOPIC,
  },
]

export const SAT_READING_TOPICS_TO_CERTIFY = [
  {
    courseName: SAT_BOOTCAMP_READING_COURSE,
    topicName: SAT_BOOTCAMP_READING_TOPIC,
  },
  {
    courseName: SAT_PREP_COURSE,
    topicName: SAT_PREP_READING_TOPIC,
  },
]

export const ELIGIBLE_TOPICS_FOR_SAT_READING_CERTIFICATION = [
  ...SAT_READING_TOPICS_TO_CERTIFY,
  {
    courseName: SAT_PREP_COURSE,
    topicName: COURSE_CHALLENGE_TOPIC,
  },
]

export const DEFAULT_SAT_BOOTCAMP_TIME_ZONE = 'America/Los_Angeles'

export const MINIMUM_TUTOR_CROSSWALK_ID = 10000000
export const MINIMUM_LEARNER_CROSSWALK_ID = 20000000

export const ORIGINAL_SAT_ASSESSMENT_TITLES = {
  math: [
    'Practice Test A1 (No Calculator)',
    'Practice Test A2 (Calculator)',
    'Practice Test A3 (Calculator)',
    'Practice Test A4 (Calculator)',
    'Practice Test B1 (No Calculator)',
    'Practice Test B2 (Calculator)',
    'Practice Test B3 (Calculator)',
  ],
  reading: [
    'Practice Test A1 (Writing)',
    'Practice Test A2 (Writing)',
    'Practice Test A3 (Reading)',
    'Practice Test A4 (Reading)',
    'Practice Test A5 (Reading)',
    'Practice Test B1 (Writing)',
    'Practice Test B2 (Reading)',
    'Practice Test B3 (Reading)',
  ],
}

export const DIGITAL_SAT_V1_ASSESSMENT_TITLES = {
  math: [
    'Practice Test 1 | Math 1A',
    'Practice Test 1 | Math 1B',
    'Practice Test 1 | Math 2A',
    'Practice Test 1 | Math 2B',
    'Practice Test 2 | Math 1',
  ],
  reading: [
    'Practice Test 1 | R&W 1A',
    'Practice Test 1 | R&W 1B',
    'Practice Test 1 | R&W 2A',
    'Practice Test 1 | R&W 2B',
    'Practice Test 2 | R&W 1',
  ],
}

export const DIGITAL_SAT_V2_ASSESSMENT_TITLES = {
  math: [
    '1 Pre-Practice Test | Math | Cycle 1',
    '1 Pre-Practice Test V1 | Math',
    '1 Pre-Practice Test V2 | Math',
    '1 Pre-Practice Test V3 | Math',
    '1 Pre-Practice Test V4 | Math',
    '2 Algebra | Cycle 1',
    '3 Advanced Math | Cycle 1',
    '4 Problem-Solving & Data Analysis | Cycle 1',
    '5 Geometry & Trigonometry | Cycle 1',
    '6 Post-Practice Test | Math | Cycle 1',
    '1 Pre-Practice Test | Math | Cycle 2',
    '2 Algebra | Cycle 2',
    '3 Advanced Math | Cycle 2',
    '4 Problem-Solving & Data Analysis | Cycle 2',
    '5 Geometry & Trigonometry | Cycle 2',
    '6 Post-Practice Test | Math | Cycle 2',
    '6 Post-Practice Test V1 | Math',
    '6 Post-Practice Test V2 | Math',
    '6 Post-Practice Test V3 | Math',
    '6 Post-Practice Test V4 | Math',
  ],
  reading: [
    '1 Pre-Practice Test | R&W | Cycle 1',
    '1 Pre-Practice Test V1 | R&W',
    '1 Pre-Practice Test V2 | R&W',
    '1 Pre-Practice Test V3 | R&W',
    '1 Pre-Practice Test V4 | R&W',
    '2 Info & Ideas | Cycle 1',
    '3 Craft & Structure | Cycle 1',
    '4 Expression of Ideas | Cycle 1',
    '5 Standard English Conventions | Cycle 1',
    '6 Post-Practice Test | R&W | Cycle 1',
    '1 Pre-Practice Test | R&W | Cycle 2',
    '2 Info & Ideas | Cycle 2',
    '3 Craft & Structure | Cycle 2',
    '4 Expression of Ideas | Cycle 2',
    '5 Standard English Conventions | Cycle 2',
    '6 Post-Practice Test | R&W | Cycle 2',
    '6 Post-Practice Test V1 | R&W',
    '6 Post-Practice Test V2 | R&W',
    '6 Post-Practice Test V3 | R&W',
    '6 Post-Practice Test V4 | R&W',
  ],
}

export const OFFCYCLE_RW_TEMPLATE_SLUG_PREFIX = 'offcycle-r&w-sat-bootcamp'
export const OFFCYCLE_MATH_TEMPLATE_SLUG_PREFIX = 'offcycle-math-sat-bootcamp'

export const SAT_BOOTCAMP_MAX_LEARNERS = 10
export const SAT_ONBOARDING_MAX_LEARNERS = 10

export const BOOTCAMP_AVERAGE_LEARNER_SPEAKING_PERCENTAGE = 17

export const JUNE_2024_SAT_TEST_TITLE = 'June 2024 SAT'

export const SATEREH_TESTIMONIAL = {
  text:
    'I did an SAT math series with Firdaus F. She was great! I really struggled to ' +
    'understand how I could get a better score on the math section until I did ' +
    'sessions with her. She is very helpful and thorough in her explanations. I had ' +
    'a very big jump in my SAT math score after doing her bootcamp series. Highly ' +
    'recommend both her and Schoolhouse as a whole!',
  speaker: 'Satereh L',
}

export const SAT_BOOTCAMP_RECENTLY_ACTIVE_MONTHS = 6

export const SAT_BOOTCAMP_MATH_SERIES_TEMPLATE_SLUG = 'sat-bootcamp-math'
export const SAT_BOOTCAMP_READING_SERIES_TEMPLATE_SLUG = 'sat-bootcamp-reading'
export const SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS = [
  SAT_BOOTCAMP_MATH_SERIES_TEMPLATE_SLUG,
  SAT_BOOTCAMP_READING_SERIES_TEMPLATE_SLUG,
]
const SAT_BOOTCAMP_1ON1_MATH_SERIES_TEMPLATE_SLUG = 'sat-bootcamp-math-1on1'
const SAT_BOOTCAMP_1ON1_READING_SERIES_TEMPLATE_SLUG = 'sat-bootcamp-reading-1on1'
export const SAT_BOOTCAMP_1ON1_SERIES_TEMPLATE_SLUGS = [
  SAT_BOOTCAMP_1ON1_MATH_SERIES_TEMPLATE_SLUG,
  SAT_BOOTCAMP_1ON1_READING_SERIES_TEMPLATE_SLUG,
]

export const SAT_BOOTCAMP_WHAT_YOULL_DO = {
  bulletsLearner: [
    {
      key: 'review-concepts',
      icon: faBookOpen,
      body: 'Review core concepts and test-taking strategies, with a peer tutor who excelled on the SAT.',
    },
    {
      key: 'practice-tests',
      icon: faDumbbell,
      body: 'Practice and review official full-length SAT® practice tests, including one Bluebook test.',
    },
    {
      key: 'personalized-support',
      icon: faComments,
      body: 'Get personalized support with the most challenging questions and concepts.',
    },
  ],
  bulletsTutor: [
    {
      key: 'review-concepts',
      icon: faBookOpen,
      body: 'Review core concepts and test-taking strategies.',
    },
    {
      key: 'practice-tests',
      icon: faDumbbell,
      body: 'Guide learners through official full-length SAT® practice tests, including one Bluebook test.',
    },
    {
      key: 'personalized-support',
      icon: faComments,
      body: 'Give personalized support with the most challenging questions and concepts.',
    },
  ],
  iconProps: { color: 'purple.solid' },
}

const SAT_BOOTCAMP_FACTOIDS = [
  { key: 'bootcamp-duration', text: '4 weeks', subtext: '2 sessions/week' },
  { key: 'session-duration', text: '75 minutes', subtext: 'Per session' },
  { key: 'max-session-capacity', text: '10 learners', subtext: 'Max class size' },
]

export const SAT_BOOTCAMP_MARKETING_CONFIG: ProgramMarketingConfig = {
  header: {
    learnerText: 'Get ready for your next SAT® in a free, 4-week bootcamp!',
    learnerSubtext:
      'Learners gain an average score of 40-55 points on their SAT after taking a bootcamp.',
    tutorText: 'Help students like you by tutoring an SAT® bootcamp!',
    backgroundImageUrl: 'sat-bootcamp/SAT-world-bg-pattern.svg',
    backgroundColor: 'purple.solid',
    factoids: SAT_BOOTCAMP_FACTOIDS,
    partnerships: [],
  },
  learnerSignUpCard: {
    signUpCardText:
      'Get ready for your next SAT® in a free, 4-week bootcamp! Bootcamps are designed for the new digital SAT.',
    signUpCardHeading: 'Sign Up',
  },
  tutorSignUpCard: {
    signUpCardHeading: 'Become a Tutor',
    signUpCardText:
      'Give back to students like you by helping others tackle their next SAT in a 4-week bootcamp.',
  },
  whatYoullDo: SAT_BOOTCAMP_WHAT_YOULL_DO,
  learnerBenefits: [
    {
      id: 'virtual',
      text: composeRichText(ctx =>
        ctx.paragraph(
          ctx.text('Enroll in a virtual 4-week small-group tutoring course '),
          ctx.text('for the digital SAT', { bold: true }),
          ctx.text('.')
        )
      ),
      faIcon: faBook,
      iconProps: { color: 'purple.contrast', backgroundColor: 'purple.solid' },
    },
    {
      id: 'score-increase',
      text: composeRichText(ctx =>
        ctx.paragraph(
          ctx.text('Feel more prepared on test day. Research shows '),
          ctx.text('significant confidence gains', { bold: true }),
          ctx.text(' for participants.')
        )
      ),
      faIcon: faHeadSideBrain,
      iconProps: { color: 'purple.contrast', backgroundColor: 'purple.solid' },
    },
    {
      id: 'personalized-peer-tutor',
      text: composeRichText(ctx =>
        ctx.paragraph(
          ctx.text('Get personalized support from our '),
          ctx.text('certified, top-scoring peer tutors', { bold: true }),
          ctx.text('.')
        )
      ),
      faIcon: faUserGroup,
      iconProps: { color: 'purple.contrast', backgroundColor: 'purple.solid' },
    },
    {
      id: 'speedy',
      text: composeRichText(ctx =>
        ctx.paragraph(
          ctx.text('Get started in 5 minutes. '),
          ctx.text('100% free and non-profit', { bold: true }),
          ctx.text('.')
        )
      ),
      faIcon: faBoltLightning,
      iconProps: { color: 'purple.contrast', backgroundColor: 'purple.solid' },
    },
  ],
  programFactoids: SAT_BOOTCAMP_FACTOIDS,
}

export const SAT_BOOTCAMP_MATH_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: 'sat-bootcamp-math',
  templateSlug: SAT_BOOTCAMP_MATH_SERIES_TEMPLATE_SLUG,
  title: 'SAT® Math Bootcamp',
  learnerDescription:
    "In interactive sessions with your tutor and other students, you'll complete multiple SAT Math practice tests and work through problems from previous SATs.",
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('programs/offerings/i-SAT-math.png'),
  wizardIcon: faCalculatorSimple,
  defaultTargetPerWeek: 100,
  targetsPerWeek: {
    12: 75,
    13: 100,
    14: 200,
    15: 700,
    16: 375,
    17: 375,
    18: 375,
    19: 700,
    20: 200,
    21: 200,
  },
}

export const SAT_BOOTCAMP_READING_WRITING_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: 'sat-bootcamp-reading-writing',
  templateSlug: SAT_BOOTCAMP_READING_SERIES_TEMPLATE_SLUG,
  title: 'SAT® Reading & Writing Bootcamp',
  learnerDescription:
    "In interactive sessions with your tutor and other students, you'll complete multiple SAT Reading & Writing practice tests and work through problems from previous SATs.",
  numWeeks: 4,
  sessionFrequency: { times: 2, per: 'week' },
  sessionLength: 75,
  imageUrl: getPublicUrl('programs/offerings/i-SAT-readingwriting-1.png'),
  wizardIcon: faPenSwirl,
  defaultTargetPerWeek: 75,
  targetsPerWeek: {
    12: 50,
    13: 75,
    14: 150,
    15: 300,
    16: 125,
    17: 125,
    18: 125,
    19: 300,
    20: 100,
    21: 100,
  },
}

export const SAT_BOOTCAMP_ONBOARDING_OFFERING_TEMPLATE: ProgramOfferingTemplate = {
  offeringSlug: 'sat-bootcamp-onboarding',
  templateSlug: SAT_BOOTCAMP_ONBOARDING_TEMPLATE_SLUG,
  hideFromLearners: true,
  title: 'SAT® Bootcamp Onboarding',
  learnerDescription: '',
  numWeeks: 1,
  sessionFrequency: { times: 1, per: 'week' },
  sessionLength: 60,
  imageUrl: getPublicUrl('programs/offerings/i-SAT-math.png'),
  wizardIcon: faChalkboardUser,
  defaultTargetPerWeek: 15,
  targetsPerWeek: {
    12: 25,
    13: 60,
    14: 60,
    15: 25,
    16: 25,
    17: 50,
    18: 50,
    19: 25,
    20: 15,
    21: 15,
  },
}

export const SAT_BOOTCAMP_PROGRAM_DETAILS: ProgramDetails = {
  slug: 'sat-bootcamp',
  title: 'SAT',
  descriptionTiny: 'Join intensive SAT prep sessions',
  descriptionShort: 'Get help preparing for your SAT',
  description: 'A four-week bootcamp to prepare for the upcoming SAT',
  worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
  fallbackWorldSlug: SAT_PREP_WORLD_SLUG,
  programType: 'bootcamp',
  learnerWaitlistName: 'sat-bootcamp-general',
  tutorWaitlistName: 'sat-bootcamp-tutor',
  partnerships: ['CollegeBoard'],
  imageUrl: '/programs/program-cards/sat-bootcamp.png',
  primaryColor: 'purple.solid',
  bgColor: 'purple.subtle',
  tutorDashboardDescription:
    'These are official SAT programs co-designed with the College Board, including 4-week Bootcamps and 75-minute Focus Sessions. Tutors use structured SAT curriculum to help learners strengthen their skills and prepare for test day.',
  offeringTemplates: [
    SAT_BOOTCAMP_READING_WRITING_OFFERING_TEMPLATE,
    SAT_BOOTCAMP_MATH_OFFERING_TEMPLATE,
    SAT_BOOTCAMP_ONBOARDING_OFFERING_TEMPLATE,
  ],
  monthlySwitchLimit: SAT_BOOTCAMP_SWITCH_MAX_PER_MONTH,
}
